/**
 * NgPrometZoneView — the view »Promet po conah« of the Promet section (#711;
 * Priloga 1 §3.3). Area choice, interval, reference period R, comparison P,
 * »Prikaži«; then the P / R / P − R blocks, the access points, the chosen
 * point, the chart, exports and the report in a new tab.
 *
 * Backed by GET /api/v1/sxapps/:public_id/promet/zones (#709). The view
 * computes no figure — it prints the answer through the shared render core
 * (ngReport/ngPrometZoneReport.ts) and tells the map what to label
 * (`ng:promet-zone-overlay`, drawn by NgPrometZoneOverlay).
 *
 * What applies when: interval, the two filters (traffic categories and
 * weekdays, #740) and the two periods wait for »Prikaži«; the area and the
 * chosen point are selections and re-ask at once, always with the periods and
 * filters of the last answer — never with edits not yet submitted.
 *
 * Public mode (#867, the page /javno/promet/<id>): the same view on the public
 * route family — no category boxes (the server always sums car + heavy + night),
 * the weekday boxes as in the internal view (#975), the period caps checked
 * before a request, no »new tab« (the report page needs a session), and
 * exports fetched so a refusal can be said in words.
 */

import type { ECharts } from 'echarts';
import type { EventBus } from '../../../../../utils/EventBus.js';
import type { PageContext } from '../../../../../services/PageContext.js';
import { DeviceDetector } from '../../../../../utils/DeviceDetector.js';
import { styleManager } from '../../../../../utils/StyleManager.js';
import type { DayFirstDateInput } from '../../../../../utils/dayFirstDateInput.js';
import { formatDayFirst } from '../../../../../../shared/utils/dayFirstDate.js';
import type {
  PrometZoneInterval,
  PrometZonesResponse,
} from '../../../../../../shared/types/ngPromet.js';
import {
  ALL_WEEKDAYS,
  DEFAULT_PROMET_CATEGORIES,
  PROMET_CATEGORIES,
  appliedFilters,
  categoryFormLabel,
  isAllWeekdays,
  normalizeCategories,
  normalizeWeekdays,
  weekdayShort,
} from '../../../../../../shared/utils/ngPrometFilters.js';
import type { PublicPeriodLimits } from '../../../../../../shared/utils/ngPrometPublic.js';
import { getEcharts } from '../echartsLoader.js';
import {
  buildZoneChartOption,
  buildZoneResultHtml,
  zoneExportBlock,
  zoneSeriesHasData,
} from '../ngReport/ngPrometZoneReport.js';
import { hourRangeLabel } from './ngPrometZoneFormat.js';
import { hourOptionsHtml, mountDayField, setFieldError } from './ngPrometPeriodFields.js';
import {
  areaColors,
  buildOverlayState,
  buildZonesQuery,
  defaultReference,
  defaultZoneFilters,
  ljubljanaHourOf,
  ljubljanaToday,
  pinnedQuery,
  reanchoredReference,
  validateZoneFilters,
  validateZonePeriods,
  type ZoneField,
  type ZoneFilterField,
  type ZoneFilters,
  type ZoneFormProblems,
  type ZoneOverlayState,
  type ZonePeriods,
  type ZoneQuery,
} from './ngPrometZoneModel.js';
import styles from './ngPrometZone.css?inline';

const COPY = {
  areaLegend: 'Območje',
  intervalLegend: 'Interval',
  intervalDay: 'dan',
  intervalHour: 'ura',
  categoriesLegend: 'Kategorije prometa',
  categoriesHint:
    'Nočni promet: kar števec zazna v temi po lučeh (večinoma motorna vozila), brez ločevanja vrst. ' +
    'Dvokolesa in pešci so šteti samo ob dnevni svetlobi.',
  weekdaysLegend: 'Dnevi v tednu',
  rLegend: 'Referenčno obdobje (R)',
  pLegendDay: 'Dan ali obdobje za primerjavo (P)',
  pLegendHour: 'Ura za primerjavo (P)',
  from: 'Od',
  to: 'Do',
  day: 'Dan',
  hour: 'Ura',
  hourNone: '— ura —',
  pHintDay: 'Prazno: zadnji razpoložljivi dan',
  pHintHour: 'Prazno: zadnja razpoložljiva ura',
  pHintDays: 'med izbranimi dnevi',
  labelsOn: 'Oznake na zemljevidu',
  submit: 'Prikaži',
  loading: 'Pridobivam podatke…',
  fixFields: 'Popravite označena polja.',
  err400: 'Preverite izbrana obdobja.',
  err403: 'Za prikaz prometa po conah potrebujete dostop do celotne aplikacije.',
  errOther: 'Podatkov ni bilo mogoče naložiti. Poskusite znova.',
  retry: 'Poskusi znova',
  fit: 'Pokaži na zemljevidu',
  openNewTab: 'Odpri v novem zavihku ↗',
  exportCsv: 'Izvozi CSV',
  exportXlsx: 'Izvozi Excel',
  exportShp: 'Izvozi SHP',
  stale: 'Vnos je spremenjen — najprej kliknite Prikaži.',
  // Public mode (#867)
  capDays: (n: number) => `Največ ${n} dni.`,
  err400Public: 'Izbrana obdobja presegajo omejitve javnega prikaza (glejte obvestilo na vrhu strani).',
  exporting: 'Pripravljam izvoz …',
  exportWait: 'Preveč prenosov v kratkem času. Počakajte minuto in poskusite znova.',
  exportFailed: 'Izvoza ni bilo mogoče pripraviti. Poskusite znova.',
};

type DateField = Exclude<ZoneField, 'pHour'>;
const DATE_FIELDS: DateField[] = ['rFrom', 'rTo', 'pFrom', 'pTo'];

/** The public page's view (#867): its route family and its period caps. */
export interface PublicZoneMode {
  /** `<public base>/zones`; the exports add `.csv` / `.xlsx` / `.shp.zip`. */
  zonesPath: string;
  limits: PublicPeriodLimits;
}

let instanceCount = 0;

export class NgPrometZoneView {
  private readonly uid = `ngz${++instanceCount}`;

  // Form
  private dates!: Record<DateField, DayFirstDateInput>;
  private interval: PrometZoneInterval = 'day';
  private area: string | null = null;
  private pointId: number | null = null;
  /** The area whose radio the mouse is over (desktop): previewed on the map. */
  private previewKey: string | null = null;
  private labelsOn = true;

  // The last answer, and its query pinned to the periods the server used
  // (pinnedQuery): what an area / point change, an export and the new tab reuse.
  private response: PrometZonesResponse | null = null;
  private submitted: ZoneQuery | null = null;
  /** The form's P was empty at the last »Prikaži«: P is »the last available«. */
  private latestP = false;
  /** True while the form holds edits »Prikaži« has not sent yet. */
  private stale = false;
  /** Counts form edits, so an answer can tell that the form moved on while it was on its way. */
  private editSeq = 0;

  /** The view is the one on screen (its overlay may be on the map). */
  private active = false;
  private started = false;
  // Guards a slow answer against a newer request.
  private seq = 0;

  private chart: ECharts | null = null;
  private chartObserver: ResizeObserver | null = null;
  /** Public mode: an export is on its way (one at a time). */
  private exporting = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly eventBus: EventBus,
    private readonly pageContext: Pick<PageContext, 'sxappId'>,
    private readonly publicMode: PublicZoneMode | null = null,
  ) {
    styleManager.injectStyles('ng-promet-zone-styles', styles);
    this.renderShell();
    this.wireEvents();
  }

  // --------------------------------------------------------------------------
  // Host API
  // --------------------------------------------------------------------------

  /** The view came on screen: load once, then show its overlay again. */
  show(): void {
    this.active = true;
    if (!this.started) {
      this.started = true;
      void this.loadDefaults();
    } else {
      this.emitOverlay();
      this.chart?.resize();
    }
  }

  /** The user switched to the other view: the map labels go with this one. */
  hide(): void {
    this.active = false;
    this.eventBus.emit('ng:promet-zone-overlay', { state: null });
  }

  /** Choose an access point (list or map); the same point again clears it. */
  selectPoint(id: number, opts: { fromMap?: boolean } = {}): void {
    if (!this.active || !this.submitted || !this.response) return;
    const area = this.response.areas.find((a) => a.key === this.area);
    if (!area?.points.some((p) => p.access_point_id === id)) return;
    const next = this.pointId === id ? null : id;
    this.pointId = next;
    // The ring on the map moves at once; the point's figures and series follow.
    this.emitOverlay();
    void this.reask(
      { ...this.submitted, area: this.area, pointId: next },
      { revealPoint: opts.fromMap === true && next !== null },
    );
  }

  destroy(): void {
    ++this.seq;
    this.active = false;
    this.eventBus.emit('ng:promet-zone-overlay', { state: null });
    this.disposeChart();
  }

  // --------------------------------------------------------------------------
  // Shell
  // --------------------------------------------------------------------------

  private el<T extends HTMLElement>(role: string): T | null {
    return this.root.querySelector<T>(`[data-role="${role}"]`);
  }

  private renderShell(): void {
    const id = (name: string): string => `${this.uid}-${name}`;
    const limits = this.publicMode?.limits;
    this.root.classList.add('ngz-view');
    this.root.innerHTML = `
      <form class="ngz-form" data-role="zone-form" novalidate>
        <fieldset class="ngz-fieldset">
          <legend class="ngz-legend">${COPY.areaLegend}</legend>
          <div class="ngz-areas" data-role="zone-areas"></div>
        </fieldset>
        <fieldset class="ngz-fieldset">
          <legend class="ngz-legend">${COPY.intervalLegend}</legend>
          <div class="ngz-interval">
            <label><input type="radio" name="${id('interval')}" value="day" checked /> ${COPY.intervalDay}</label>
            <label><input type="radio" name="${id('interval')}" value="hour" /> ${COPY.intervalHour}</label>
          </div>
        </fieldset>
        ${this.publicMode ? this.weekdaysFieldsetHtml() : this.categoriesFieldsetHtml() + this.weekdaysFieldsetHtml()}
        <fieldset class="ngz-fieldset">
          <legend class="ngz-legend">${COPY.rLegend}</legend>
          <div class="ngz-dates">
            <div class="ngz-field" data-field="rFrom"><label for="${id('rFrom')}">${COPY.from}</label></div>
            <div class="ngz-field" data-field="rTo"><label for="${id('rTo')}">${COPY.to}</label></div>
          </div>
          ${limits ? `<p class="ngz-hint">${COPY.capDays(limits.referenceDays)}</p>` : ''}
        </fieldset>
        <fieldset class="ngz-fieldset">
          <legend class="ngz-legend" data-role="p-legend">${COPY.pLegendDay}</legend>
          <div class="ngz-dates">
            <div class="ngz-field" data-field="pFrom"><label for="${id('pFrom')}" data-role="p-from-label">${COPY.from}</label></div>
            <div class="ngz-field" data-field="pTo"><label for="${id('pTo')}">${COPY.to}</label></div>
            <div class="ngz-field" data-field="pHour" hidden>
              <label for="${id('pHour')}">${COPY.hour}</label>
              <select class="ngz-select" id="${id('pHour')}" data-role="p-hour" aria-describedby="${id('pHour-err')}">
                ${hourOptionsHtml(COPY.hourNone)}
              </select>
              <p class="ngz-field-error" id="${id('pHour-err')}" data-error="pHour" hidden></p>
            </div>
          </div>
          <p class="ngz-hint" data-role="p-hint"></p>
          ${limits ? `<p class="ngz-hint" data-role="p-cap">${COPY.capDays(limits.comparisonDays)}</p>` : ''}
        </fieldset>
        <label class="ngz-check"><input type="checkbox" data-role="labels-on" checked /> ${COPY.labelsOn}</label>
        <div class="ngz-submit-row">
          <button type="submit" class="ngz-submit" data-role="zone-submit">${COPY.submit}</button>
          <span class="ngz-status" role="status" data-role="zone-status"></span>
        </div>
      </form>
      <div data-role="zone-result"></div>
    `;

    const dates = {} as Record<DateField, DayFirstDateInput>;
    for (const field of DATE_FIELDS) {
      const host = this.root.querySelector<HTMLElement>(`[data-field="${field}"]`);
      dates[field] = mountDayField(host, id(field), field);
    }
    this.dates = dates;
    this.renderPeriodHint();
  }

  /** The category group (#740) — the internal view only; the public page keeps the sum (#867). */
  private categoriesFieldsetHtml(): string {
    const id = (name: string): string => `${this.uid}-${name}`;
    return `
        <fieldset class="ngz-fieldset" data-field="categories" aria-describedby="${id('cat-hint')} ${id('cat-err')}">
          <legend class="ngz-legend">${COPY.categoriesLegend}</legend>
          <div class="ngz-checks">
            ${PROMET_CATEGORIES.map(
              (c) =>
                `<label><input type="checkbox" data-role="zone-cat" value="${c}"${DEFAULT_PROMET_CATEGORIES.includes(c) ? ' checked' : ''} /> ${categoryFormLabel(c)}</label>`,
            ).join('')}
          </div>
          <p class="ngz-hint" id="${id('cat-hint')}">${COPY.categoriesHint}</p>
          <p class="ngz-field-error" id="${id('cat-err')}" data-error="categories" hidden></p>
        </fieldset>`;
  }

  /** The weekday group (#740) — the internal view and, since #975, the public page. */
  private weekdaysFieldsetHtml(): string {
    const id = (name: string): string => `${this.uid}-${name}`;
    return `
        <fieldset class="ngz-fieldset" data-field="weekdays" aria-describedby="${id('day-err')}">
          <legend class="ngz-legend">${COPY.weekdaysLegend}</legend>
          <div class="ngz-checks ngz-days">
            ${ALL_WEEKDAYS.map(
              (d) => `<label><input type="checkbox" data-role="zone-day" value="${d}" checked /> ${weekdayShort(d)}</label>`,
            ).join('')}
          </div>
          <p class="ngz-field-error" id="${id('day-err')}" data-error="weekdays" hidden></p>
        </fieldset>`;
  }

  private renderAreas(): void {
    const host = this.el('zone-areas');
    if (!host || !this.response) return;
    // The same areas as last time (every answer but the first): only the
    // checked state moves. Rebuilding the radios would drop the keyboard focus
    // of someone walking the areas with the arrow keys.
    const keys = this.response.areas.map((a) => a.key).join('|');
    if (host.dataset.areaKeys === keys) {
      host.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === this.area;
      });
      return;
    }
    host.dataset.areaKeys = keys;
    const name = `${this.uid}-area`;
    // The swatch is the area's colour on the map: the radios double as its legend.
    const colors = areaColors(this.response.areas);
    host.innerHTML = this.response.areas
      .map(
        (a) =>
          `<label data-area-key="${escapeAttr(a.key)}"><input type="radio" name="${name}" value="${escapeAttr(a.key)}"${a.key === this.area ? ' checked' : ''} />` +
          `<span class="ngz-swatch" style="background:${colors.get(a.key) ?? 'transparent'}" aria-hidden="true"></span> ${escapeHtml(a.name)}</label>`,
      )
      .join('');
    this.previewKey = null;
    // Pointing at a radio (or reaching it by keyboard) previews its area on
    // the map. Mouse devices only: a touch has no hover, and a tap would
    // preview what it is about to choose.
    if (DeviceDetector.getDeviceType() !== 'desktop') return;
    host.querySelectorAll<HTMLElement>('label[data-area-key]').forEach((label) => {
      const key = label.dataset.areaKey ?? null;
      const on = (): void => this.setPreview(key);
      const off = (): void => this.setPreview(null);
      label.addEventListener('mouseenter', on);
      label.addEventListener('mouseleave', off);
      label.querySelector('input')?.addEventListener('focus', on);
      label.querySelector('input')?.addEventListener('blur', off);
    });
  }

  private setPreview(key: string | null): void {
    if (this.previewKey === key) return;
    this.previewKey = key;
    this.emitOverlay();
  }

  /** P's fields follow the interval: a day range, or one day and an hour. */
  private renderPeriodFields(): void {
    const hourly = this.interval === 'hour';
    const set = (field: string, hidden: boolean): void => {
      const el = this.root.querySelector<HTMLElement>(`[data-field="${field}"]`);
      if (el) el.hidden = hidden;
    };
    set('pTo', hourly);
    set('pHour', !hourly);
    // Public mode: an hour P is one hour, the cap on P's days says nothing then.
    const pCap = this.el('p-cap');
    if (pCap) pCap.hidden = hourly;
    const legend = this.el('p-legend');
    if (legend) legend.textContent = hourly ? COPY.pLegendHour : COPY.pLegendDay;
    const fromLabel = this.el('p-from-label');
    if (fromLabel) fromLabel.textContent = hourly ? COPY.day : COPY.from;
    this.renderPeriodHint();
  }

  /**
   * »Prazno: zadnji razpoložljivi dan (1. 8. 2026).« — from the last answer.
   * With weekdays chosen in the form the default P is the last day of one of
   * THEM (#740): the hint says so, and names the date only while the answer
   * on screen was asked with the same days.
   */
  private renderPeriodHint(): void {
    const hint = this.el('p-hint');
    if (!hint) return;
    const days = this.readFilters().weekdays;
    const filtered = days.length > 0 && !isAllWeekdays(days);
    const last = this.formDaysAreAnswerDays() ? this.response?.last_available : undefined;
    const words = (base: string): string => (filtered ? `${base} ${COPY.pHintDays}` : base);
    if (this.interval === 'hour') {
      const at = last?.hour ? ljubljanaHourOf(last.hour) : null;
      hint.textContent = at
        ? `${words(COPY.pHintHour)} (${formatDayFirst(at.day)} ${hourRangeLabel(at.hour)}).`
        : `${words(COPY.pHintHour)}.`;
    } else {
      hint.textContent = last?.day ? `${words(COPY.pHintDay)} (${formatDayFirst(last.day)}).` : `${words(COPY.pHintDay)}.`;
    }
  }

  // --------------------------------------------------------------------------
  // Events
  // --------------------------------------------------------------------------

  private wireEvents(): void {
    this.el<HTMLFormElement>('zone-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitForm();
    });

    for (const field of DATE_FIELDS) {
      const widget = this.dates[field];
      widget.input.addEventListener('input', () => {
        this.setFieldError(field, null);
        this.noteEdit();
      });
      widget.input.addEventListener('blur', () => {
        const why = widget.problem();
        if (why) this.setFieldError(field, why);
      });
    }

    this.el<HTMLSelectElement>('p-hour')?.addEventListener('change', () => {
      this.setFieldError('pHour', null);
      this.noteEdit();
    });

    this.root.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.name === `${this.uid}-interval` && target.checked) {
        this.interval = target.value === 'hour' ? 'hour' : 'day';
        // The two intervals mean different things by P: start from »last available«.
        this.dates.pFrom.setIso(null);
        this.dates.pTo.setIso(null);
        const hour = this.el<HTMLSelectElement>('p-hour');
        if (hour) hour.value = '';
        for (const f of ['pFrom', 'pTo', 'pHour'] as ZoneField[]) this.setFieldError(f, null);
        this.renderPeriodFields();
        this.noteEdit();
      } else if (target.name === `${this.uid}-area` && target.checked) {
        this.onAreaChosen(target.value);
      } else if (target.dataset.role === 'zone-cat' || target.dataset.role === 'zone-day') {
        // A filter edit waits for »Prikaži«, like the interval and the periods.
        this.setFilterError(target.dataset.role === 'zone-cat' ? 'categories' : 'weekdays', null);
        this.renderPeriodHint();
        this.noteEdit();
      } else if (target.dataset.role === 'labels-on') {
        this.labelsOn = target.checked;
        this.emitOverlay();
      }
    });

    // The result is re-rendered on every answer: one delegated listener.
    this.root.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('[data-role]');
      if (!target || !this.root.contains(target)) return;
      const role = target.dataset.role;
      if (role === 'zone-point') {
        this.selectPoint(Number(target.dataset.pointId));
      } else if (role === 'zone-fit') {
        // »Show on the map« with the labels switched off switches them on.
        if (!this.labelsOn) {
          this.labelsOn = true;
          const box = this.el<HTMLInputElement>('labels-on');
          if (box) box.checked = true;
          this.emitOverlay();
        }
        this.eventBus.emit('ng:promet-zone-fit', {});
      } else if (role === 'zone-retry') {
        void this.loadDefaults();
      } else if (role === 'zone-newtab' || role === 'zone-csv' || role === 'zone-xlsx' || role === 'zone-shp') {
        // aria-disabled keeps the button focusable; the click does nothing.
        if (target.getAttribute('aria-disabled') === 'true') return;
        if (role === 'zone-newtab') this.openNewTab();
        else this.download(role === 'zone-csv' ? 'csv' : role === 'zone-xlsx' ? 'xlsx' : 'shp.zip');
      }
    });
  }

  /** The area radio: the map follows at once, the figures with the answer. */
  private onAreaChosen(key: string): void {
    this.area = key;
    this.emitOverlay();
    if (this.submitted) void this.reask({ ...this.submitted, area: key });
  }

  /** An interval / period edit: the result on screen no longer matches the form. */
  private noteEdit(): void {
    this.editSeq++;
    if (this.stale) return;
    this.stale = true;
    this.updateActions();
  }

  // --------------------------------------------------------------------------
  // Form → query
  // --------------------------------------------------------------------------

  private setFieldError(field: ZoneField, message: string | null): void {
    const error = this.root.querySelector<HTMLElement>(`[data-error="${field}"]`);
    const control: HTMLElement | null =
      field === 'pHour' ? this.el('p-hour') : this.dates[field].input;
    setFieldError(control, error, message);
  }

  /**
   * The two checkbox groups as ticked, in canonical order. Public mode has no
   * category boxes: the categories are always the default, the days as ticked (#975).
   */
  private readFilters(): ZoneFilters {
    const ticked = (role: string): string[] =>
      [...this.root.querySelectorAll<HTMLInputElement>(`input[data-role="${role}"]`)]
        .filter((box) => box.checked)
        .map((box) => box.value);
    return {
      categories: this.publicMode ? defaultZoneFilters().categories : normalizeCategories(ticked('zone-cat')),
      weekdays: normalizeWeekdays(ticked('zone-day').map(Number)),
    };
  }

  /**
   * The days ticked in the form are the days the answer on screen was asked
   * with. Only then does the answer's `last_available` stand for an empty P —
   * under a weekday filter it is the last complete day OF THOSE DAYS (#740).
   */
  private formDaysAreAnswerDays(): boolean {
    const days = this.readFilters().weekdays;
    const asked = this.response ? appliedFilters(this.response.applied).weekdays : [...ALL_WEEKDAYS];
    return days.length === asked.length && days.every((d, i) => d === asked[i]);
  }

  /** Tick exactly the given boxes of both groups. */
  private writeFilters(filters: ZoneFilters): void {
    const set = (role: string, on: (value: string) => boolean): void => {
      this.root.querySelectorAll<HTMLInputElement>(`input[data-role="${role}"]`).forEach((box) => {
        box.checked = on(box.value);
      });
    };
    set('zone-cat', (v) => (filters.categories as string[]).includes(v));
    set('zone-day', (v) => filters.weekdays.includes(Number(v)));
  }

  /** The error of a checkbox group: its text, and `aria-invalid` on every box of the group. */
  private setFilterError(field: ZoneFilterField, message: string | null): void {
    const error = this.root.querySelector<HTMLElement>(`[data-error="${field}"]`);
    if (error) {
      error.textContent = message ?? '';
      error.hidden = !message;
    }
    const role = field === 'categories' ? 'zone-cat' : 'zone-day';
    this.root.querySelectorAll<HTMLInputElement>(`input[data-role="${role}"]`).forEach((box) => {
      if (message) box.setAttribute('aria-invalid', 'true');
      else box.removeAttribute('aria-invalid');
    });
  }

  private readPeriods(): { periods: ZonePeriods; problems: ZoneFormProblems } {
    const hourly = this.interval === 'hour';
    const hourValue = this.el<HTMLSelectElement>('p-hour')?.value ?? '';
    const periods: ZonePeriods = {
      interval: this.interval,
      rFrom: this.dates.rFrom.iso(),
      rTo: this.dates.rTo.iso(),
      pFrom: this.dates.pFrom.iso(),
      pTo: hourly ? null : this.dates.pTo.iso(),
      pHour: hourly && hourValue !== '' ? Number(hourValue) : null,
    };
    // A text that is not a date outranks »empty«: say what is wrong with it.
    const problems: ZoneFormProblems = validateZonePeriods(periods, this.publicMode?.limits, this.lastAvailableDay());
    for (const field of DATE_FIELDS) {
      if (field === 'pTo' && hourly) continue;
      const why = this.dates[field].problem();
      if (why) problems[field] = why;
    }
    return { periods, problems };
  }

  /**
   * The day an empty P stands for: the last available day (or the day of the
   * last hour) of the answer on screen. Unknown while the form's days differ
   * from the answer's (#975 D4): the public window cap is then the server's alone.
   */
  private lastAvailableDay(): string | null {
    const last = this.response?.last_available;
    if (!last || !this.formDaysAreAnswerDays()) return null;
    if (this.interval === 'hour') return last.hour ? (ljubljanaHourOf(last.hour)?.day ?? null) : null;
    return last.day;
  }

  /** The zone route: the public family in public mode, else the app's own. */
  private zonesPath(): string {
    return this.publicMode?.zonesPath ?? `/api/v1/sxapps/${encodeURIComponent(this.pageContext.sxappId)}/promet/zones`;
  }

  private submitForm(): void {
    const { periods, problems } = this.readPeriods();
    const filters = this.readFilters();
    const filterProblems = validateZoneFilters(filters);
    const groups: ZoneFilterField[] = ['categories', 'weekdays'];
    for (const g of groups) this.setFilterError(g, filterProblems[g] ?? null);
    const fields: ZoneField[] = ['rFrom', 'rTo', 'pFrom', 'pTo', 'pHour'];
    for (const f of fields) this.setFieldError(f, problems[f] ?? null);
    // The groups come before the periods in the form: the first bad one gets the focus.
    const badGroup = groups.find((g) => filterProblems[g]);
    const firstBad = fields.find((f) => problems[f]);
    if (badGroup || firstBad || !periods.rFrom || !periods.rTo) {
      this.setStatus(COPY.fixFields, true);
      const control: HTMLElement | null = badGroup
        ? this.root.querySelector<HTMLElement>(`input[data-role="${badGroup === 'categories' ? 'zone-cat' : 'zone-day'}"]`)
        : firstBad === 'pHour'
          ? this.el('p-hour')
          : firstBad
            ? this.dates[firstBad].input
            : null;
      control?.focus();
      return;
    }
    void this.submitQuery({
      categories: filters.categories,
      weekdays: filters.weekdays,
      interval: periods.interval,
      rFrom: periods.rFrom,
      rTo: periods.rTo,
      pFrom: periods.pFrom,
      pTo: periods.pTo,
      pHour: periods.pHour,
      area: this.area,
      // A point survives new periods while it belongs to the area.
      pointId: this.pointId,
    });
  }

  // --------------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------------

  /**
   * First load. R = the 30 days ending yesterday; when the data ends before
   * that (dev's frozen copy), R moves onto the last day with data and the
   * request is repeated once. P is left to the server (last available).
   */
  private async loadDefaults(): Promise<void> {
    const reference = defaultReference(ljubljanaToday());
    // The boxes as they stand: a retry after a failed first load may follow an
    // edit. A group with nothing ticked cannot be asked — its default goes back
    // INTO the boxes first, so the form always shows what the answer is for.
    let filters = this.readFilters();
    const empty = validateZoneFilters(filters);
    if (empty.categories || empty.weekdays) {
      const start = defaultZoneFilters();
      this.writeFilters({
        categories: empty.categories ? start.categories : filters.categories,
        weekdays: empty.weekdays ? start.weekdays : filters.weekdays,
      });
      filters = this.readFilters();
      this.setFilterError('categories', null);
      this.setFilterError('weekdays', null);
      this.renderPeriodHint();
    }
    const base: ZoneQuery = {
      interval: this.interval,
      rFrom: reference.from,
      rTo: reference.to,
      pFrom: null,
      pTo: null,
      pHour: null,
      area: this.area,
      pointId: null,
      categories: filters.categories,
      weekdays: filters.weekdays,
    };
    const edits = this.editSeq;
    const first = await this.request(base);
    if (typeof first === 'string') return;
    const moved = reanchoredReference(first.last_available.day, reference);
    // The user typed into the form while the first answer was on its way:
    // their periods stand, the default R is not moved under them.
    if (!moved || edits !== this.editSeq) {
      this.accept(base, first, { fromForm: true, edits });
      return;
    }
    const query = { ...base, rFrom: moved.from, rTo: moved.to };
    const second = await this.request(query);
    if (second === 'overtaken') return;
    if (second === 'failed') this.accept(base, first, { fromForm: true, edits });
    else this.accept(query, second, { fromForm: true, edits });
  }

  /** »Prikaži«: the form's periods; an empty P asks for the last available again. */
  private async submitQuery(query: ZoneQuery): Promise<void> {
    const edits = this.editSeq;
    const data = await this.request(query);
    if (typeof data !== 'string') this.accept(query, data, { fromForm: true, edits });
  }

  /** An area or point change: the periods of the answer on screen, nothing from the form. */
  private async reask(query: ZoneQuery, opts: { revealPoint?: boolean } = {}): Promise<void> {
    const data = await this.request(query);
    if (typeof data !== 'string') {
      this.accept(query, data, opts);
    } else if (data === 'failed' && this.submitted) {
      // The choice did not go through: the radio and the map go back to the
      // area and point of the answer that is still on screen.
      this.area = this.submitted.area;
      this.pointId = this.submitted.pointId;
      this.renderAreas();
      this.emitOverlay();
    }
  }

  /**
   * One request. 'failed' = the status line says why; 'overtaken' = a newer
   * request started meanwhile and owns the screen now.
   */
  private async request(query: ZoneQuery): Promise<PrometZonesResponse | 'failed' | 'overtaken'> {
    const seq = ++this.seq;
    const button = this.el<HTMLButtonElement>('zone-submit');
    if (button) button.disabled = true;
    this.setStatus(COPY.loading, false);

    try {
      const url = `${this.zonesPath()}?` + buildZonesQuery(query);
      const resp = await fetch(url, { credentials: 'include' });
      if (seq !== this.seq) return 'overtaken';
      if (!resp.ok) {
        // Never the server's own error text: it is a status name or English.
        console.warn('[NgPrometZoneView] zones request failed', resp.status);
        const err400 = this.publicMode ? COPY.err400Public : COPY.err400;
        this.showLoadError(resp.status === 400 ? err400 : resp.status === 403 ? COPY.err403 : COPY.errOther);
        return 'failed';
      }
      const data = (await resp.json()) as PrometZonesResponse;
      if (seq !== this.seq) return 'overtaken';
      this.setStatus('', false);
      return data;
    } catch (err) {
      if (seq !== this.seq) return 'overtaken';
      console.error('[NgPrometZoneView] zones request threw', err);
      this.showLoadError(COPY.errOther);
      return 'failed';
    } finally {
      if (seq === this.seq && button) button.disabled = false;
    }
  }

  /** Take an answer as the result on screen. */
  private accept(
    query: ZoneQuery,
    data: PrometZonesResponse,
    opts: { fromForm?: boolean; edits?: number; revealPoint?: boolean } = {},
  ): void {
    this.response = data;
    // What the server actually used: a defaulted area, a dropped point, the
    // comparison period it resolved for an empty P.
    this.area = data.area;
    this.pointId = data.access_point_id;
    this.submitted = pinnedQuery(query, data);
    if (opts.fromForm) {
      this.latestP = query.pFrom === null && data.comparison?.defaulted === true;
      // Edits typed while the request was on its way stay in the boxes, and
      // the result stays »stale« against them.
      if (opts.edits === this.editSeq) {
        this.stale = false;
        // The boxes show the dates as the request read them (»1.7.26« → »1. 7. 2026«).
        this.dates.rFrom.setIso(query.rFrom);
        this.dates.rTo.setIso(query.rTo);
        this.dates.pFrom.setIso(query.pFrom);
        this.dates.pTo.setIso(query.pTo);
      }
    }
    this.renderAreas();
    this.renderPeriodHint();
    this.renderResult();
    if (opts.revealPoint) this.el('zone-point-block')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private setStatus(text: string, isError: boolean): void {
    const status = this.el('zone-status');
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('is-error', isError);
  }

  /** A failed load keeps the last result on screen; with none, it offers a retry. */
  private showLoadError(message: string): void {
    const status = this.el('zone-status');
    if (!status) return;
    status.classList.add('is-error');
    status.textContent = message;
    if (!this.response) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ngz-link';
      retry.dataset.role = 'zone-retry';
      retry.textContent = COPY.retry;
      status.append(' ', retry);
    }
  }

  // --------------------------------------------------------------------------
  // Result
  // --------------------------------------------------------------------------

  private renderResult(): void {
    const host = this.el('zone-result');
    if (!host || !this.response) return;
    const noteId = `${this.uid}-action-note`;
    // The report page needs a session: no new tab on the public page (#867).
    const newTab = this.publicMode
      ? ''
      : `<button type="button" class="ngz-action" data-role="zone-newtab" aria-describedby="${noteId}">${COPY.openNewTab}</button>`;
    const actionsHtml = `<div class="ngz-actions">
        <button type="button" class="ngz-action" data-role="zone-fit">${COPY.fit}</button>
        ${newTab}
        <button type="button" class="ngz-action" data-role="zone-csv" aria-describedby="${noteId}">${COPY.exportCsv}</button>
        <button type="button" class="ngz-action" data-role="zone-xlsx" aria-describedby="${noteId}">${COPY.exportXlsx}</button>
        <button type="button" class="ngz-action" data-role="zone-shp" aria-describedby="${noteId}">${COPY.exportShp}</button>
        <span class="ngz-action-note" id="${noteId}" role="status" data-role="zone-action-note"></span>
      </div>`;

    // The chart node is replaced with the rest: drop the old instance first.
    this.disposeChart();
    host.innerHTML = buildZoneResultHtml(this.response, {
      actionsHtml,
      selectable: true,
      listFirst: DeviceDetector.getDeviceType() === 'phone',
      lastAvailable: this.latestP,
    });
    this.updateActions();
    this.emitOverlay();
    void this.renderChart(this.response);
  }

  /**
   * Exports and the new tab describe the answer on screen: off while the form
   * has unsent edits, and an export off when the server would refuse its size.
   * aria-disabled + the note (aria-describedby) say why.
   */
  private updateActions(): void {
    const block = (ext: 'csv' | 'xlsx'): string => (this.response ? zoneExportBlock(this.response, ext) : '');
    const csvBlock = block('csv');
    const xlsxBlock = block('xlsx');
    this.el('zone-newtab')?.setAttribute('aria-disabled', String(this.stale));
    this.el('zone-csv')?.setAttribute('aria-disabled', String(this.stale || csvBlock !== ''));
    this.el('zone-xlsx')?.setAttribute('aria-disabled', String(this.stale || xlsxBlock !== ''));
    // SHP holds the areas and points with their figures, no series: no size check (#736).
    this.el('zone-shp')?.setAttribute('aria-disabled', String(this.stale));
    const note = this.el('zone-action-note');
    if (note) note.textContent = this.stale ? COPY.stale : csvBlock || xlsxBlock;
  }

  private submittedQuery(): string | null {
    return this.submitted ? buildZonesQuery(this.submitted) : null;
  }

  private openNewTab(): void {
    const qs = this.submittedQuery();
    if (!qs) return;
    // noopener: a fresh browsing context, the map page is never navigated.
    window.open(
      `/ng-report?app=${encodeURIComponent(this.pageContext.sxappId)}&view=promet-zones&${qs}`,
      '_blank',
      'noopener',
    );
  }

  private download(ext: 'csv' | 'xlsx' | 'shp.zip'): void {
    const qs = this.submittedQuery();
    if (!qs) return;
    const url = `${this.zonesPath()}.${ext}?${qs}`;
    if (this.publicMode) {
      void this.downloadChecked(url, ext);
      return;
    }
    // <a download>, never window.location: a navigation would fire the map
    // page's beforeunload teardown before the browser sees the attachment.
    saveHref(url, '');
  }

  /**
   * Public mode: fetch the export first, so a refused one (the per-IP export
   * limit, a server error) is said in the note instead of ending as a failed
   * download; the answer on screen stays.
   */
  private async downloadChecked(url: string, ext: 'csv' | 'xlsx' | 'shp.zip'): Promise<void> {
    if (this.exporting) return;
    this.exporting = true;
    const note = this.el('zone-action-note');
    if (note) note.textContent = COPY.exporting;
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn('[NgPrometZoneView] export refused', resp.status);
        if (note) note.textContent = resp.status === 429 ? COPY.exportWait : COPY.exportFailed;
        return;
      }
      const blob = await resp.blob();
      const name = filenameFromDisposition(resp.headers.get('content-disposition')) ?? `promet-cone.${ext}`;
      const href = URL.createObjectURL(blob);
      saveHref(href, name);
      // Some browsers start reading the blob only after the click event: release it later.
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
      this.updateActions();
    } catch (err) {
      console.error('[NgPrometZoneView] export threw', err);
      if (note) note.textContent = COPY.exportFailed;
    } finally {
      this.exporting = false;
    }
  }

  // --------------------------------------------------------------------------
  // Map overlay
  // --------------------------------------------------------------------------

  private overlayState(): ZoneOverlayState | null {
    if (!this.active || !this.labelsOn || !this.area || !this.response) return null;
    // One answer holds the figures of all four areas, so a newly chosen area
    // has its labels at once, before its own answer arrives.
    return buildOverlayState(this.response, this.area, this.pointId, this.previewKey);
  }

  private emitOverlay(): void {
    this.eventBus.emit('ng:promet-zone-overlay', { state: this.overlayState() });
  }

  // --------------------------------------------------------------------------
  // Chart (the shared core builds the option; the view owns the instance)
  // --------------------------------------------------------------------------

  private async renderChart(response: PrometZonesResponse): Promise<void> {
    const container = this.el('zone-chart');
    const empty = this.el('zone-chart-empty');
    if (!container || !empty) return;
    if (!zoneSeriesHasData(response.series)) {
      container.hidden = true;
      empty.hidden = false;
      return;
    }
    const echarts = await getEcharts();
    // A newer answer replaced the node while ECharts was loading.
    if (this.response !== response || !container.isConnected) return;
    this.chart = echarts.init(container);
    this.chart.setOption(buildZoneChartOption(response), { notMerge: true });
    this.chart.resize();
    if (typeof ResizeObserver !== 'undefined') {
      this.chartObserver = new ResizeObserver(() => this.chart?.resize());
      this.chartObserver.observe(container);
    }
  }

  private disposeChart(): void {
    this.chartObserver?.disconnect();
    this.chartObserver = null;
    this.chart?.dispose();
    this.chart = null;
  }
}

/** A hidden `<a download>` click: the browser saves `href` (an empty name = the server's). */
function saveHref(href: string, name: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** The file name of `attachment; filename="…"` (the export names are ASCII). */
export function filenameFromDisposition(header: string | null): string | null {
  const m = header ? /filename="([^"]+)"/.exec(header) : null;
  return m?.[1] ?? null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const escapeAttr = escapeHtml;
